import { useEffect, useCallback } from 'react';
import type { Store } from '../services/types';
export type SearchFormData = {
  query: string;
};

export type SearchOverrideProps = {
  onSearch: (data: SearchFormData) => void;
  store?: Store;
  enabled?: boolean;
};

const getStoreSelectors = (store: Store) => {
  if (
    store.config.config?.searchConfig?.formSelector &&
    store.config.config?.searchConfig?.inputSelector
  ) {
    return {
      formSelector: store.config.config.searchConfig.formSelector,
      inputSelector: store.config.config.searchConfig.inputSelector,
    };
  }

  return { formSelector: null, inputSelector: null };
};

export const useStoreSearchOverride = ({
  store,
  onSearch,
  enabled = false,
}: SearchOverrideProps) => {
  const handleSubmit = useCallback(
    (event: Event, inputElement: HTMLInputElement) => {
      event.preventDefault();
      event.stopPropagation();

      onSearch({
        query: inputElement.value,
      });
    },
    [onSearch]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: This effect intentionally doesn't include all dependencies
  useEffect(() => {
    if (!enabled) return;
    if (!store?.id) return;

    const { formSelector, inputSelector } = getStoreSelectors(store);
    if (!formSelector || !inputSelector) return;

    const form = document.querySelector(formSelector);

    if (!form) {
      console.warn(`Search form not found with selector: ${formSelector}`);
      return;
    }

    const searchInput = form.querySelector(
      inputSelector
    ) as HTMLInputElement | null;
    if (!searchInput) {
      console.warn(`Search input not found with selector: ${inputSelector}`);
      return;
    }

    const submitHandler = (event: Event) => {
      handleSubmit(event, searchInput);
    };

    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSubmit(event, searchInput);
      }
    };

    form.addEventListener('submit', submitHandler, true);
    searchInput.addEventListener('keydown', keydownHandler, true);

    return () => {
      form.removeEventListener('submit', submitHandler, true);
      searchInput.removeEventListener('keydown', keydownHandler, true);
    };
  }, [store, enabled]);
};
